import {
  useMutation,
  useQuery,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { SupabaseClient } from '../supabase/client';
import type {
  CourierProofSubmissionResult,
  PublicCourierManifestResult,
  PublicCourierPortalResult,
  SubmitCourierDeliveryProofInput,
  SubmitCourierPortalDeliveryProofInput,
} from '../types/courier';
import {
  getPublicCourierManifest,
  getPublicCourierPortal,
  submitCourierDeliveryProof,
  submitCourierPortalDeliveryProof,
} from '../services/courier.service';

export const courierKeys = {
  all: ['couriers'] as const,
  list: (storeId: string | null) => ['couriers', 'list', storeId] as const,
  batches: (storeId: string | null) => ['couriers', 'batches', storeId] as const,
  proof: (storeId: string | null, proofId: string | null) =>
    ['couriers', 'proof', storeId, proofId] as const,
  manifest: (
    batchId: string | null,
    secretPresenceKey: 'present' | 'missing',
    secretCacheKey: string | number = 'stable',
  ) => ['couriers', 'public-manifest', batchId, secretPresenceKey, secretCacheKey] as const,
  portalLink: (storeId: string | null, courierId: string | null) =>
    ['couriers', 'portal-link', storeId, courierId] as const,
  publicPortal: (
    portalLinkId: string | null,
    secretPresenceKey: 'present' | 'missing',
    secretCacheKey: string | number = 'stable',
  ) => ['couriers', 'public-portal', portalLinkId, secretPresenceKey, secretCacheKey] as const,
};

export function usePublicCourierManifest(
  client: SupabaseClient,
  batchId: string | undefined,
  rawSecret: string | undefined,
  secretCacheKey: string | number = 'stable',
): UseQueryResult<PublicCourierManifestResult> {
  const hasSecret = Boolean(rawSecret);
  const enabled = Boolean(batchId && rawSecret);
  return useQuery({
    queryKey: courierKeys.manifest(
      batchId ?? null,
      hasSecret ? 'present' : 'missing',
      secretCacheKey,
    ),
    enabled,
    queryFn: () => getPublicCourierManifest(client, batchId as string, rawSecret as string),
  });
}

export function usePublicCourierPortal(
  client: SupabaseClient,
  portalLinkId: string | undefined,
  rawSecret: string | undefined,
  secretCacheKey: string | number = 'stable',
): UseQueryResult<PublicCourierPortalResult> {
  const hasSecret = Boolean(rawSecret);
  const enabled = Boolean(portalLinkId && rawSecret);
  return useQuery({
    queryKey: courierKeys.publicPortal(
      portalLinkId ?? null,
      hasSecret ? 'present' : 'missing',
      secretCacheKey,
    ),
    enabled,
    queryFn: () => getPublicCourierPortal(client, portalLinkId as string, rawSecret as string),
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });
}

export function useSubmitCourierDeliveryProof(
  client: SupabaseClient,
): UseMutationResult<CourierProofSubmissionResult, unknown, SubmitCourierDeliveryProofInput> {
  return useMutation({
    mutationFn: (input) => submitCourierDeliveryProof(client, input),
  });
}

export function useSubmitCourierPortalDeliveryProof(
  client: SupabaseClient,
): UseMutationResult<CourierProofSubmissionResult, unknown, SubmitCourierPortalDeliveryProofInput> {
  return useMutation({
    mutationFn: (input) => submitCourierPortalDeliveryProof(client, input),
  });
}
